/**
 * DoctorReviewActions Component
 *
 * Action buttons for doctor lab result review flow.
 * Allows doctor to approve or reject lab results with notes.
 */
import { useState } from 'react'
import {
  Card,
  Input,
  Button,
  Space,
  Typography,
  Alert,
  message,
} from 'antd'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  MedicineBoxOutlined,
} from '@ant-design/icons'

const { TextArea } = Input
const { Title } = Typography

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3520/api'

interface DoctorReviewActionsProps {
  labResultId: string
  onSuccess: () => void
  patientName?: string
}

/**
 * Submit doctor review
 */
async function submitReview(
  token: string,
  labResultId: string,
  decision: 'APPROVED' | 'REJECTED',
  reviewNotes?: string,
): Promise<void> {
  const response = await fetch(`${API_URL}/admin/lab-results/${labResultId}/review`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      decision,
      reviewNotes,
    }),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.message || 'Failed to submit review')
  }
}

/**
 * Main DoctorReviewActions Component
 */
export function DoctorReviewActions({
  labResultId,
  onSuccess,
  patientName,
}: DoctorReviewActionsProps) {
  const [notes, setNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (decision: 'APPROVED' | 'REJECTED') => {
    const token = localStorage.getItem('admin_access_token')
    if (!token) {
      message.error('Niste prijavljeni')
      return
    }

    setSubmitting(true)

    try {
      await submitReview(token, labResultId, decision, notes)

      if (decision === 'APPROVED') {
        message.success('Rezultati odobreni - pacijent može zatražiti konsultaciju')
      } else {
        message.warning('Rezultati odbijeni')
      }

      onSuccess()
    } catch (error) {
      console.error('Review failed:', error)
      message.error(
        `Greška: ${error instanceof Error ? error.message : 'Neuspeo pregled'}`,
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Card
      title={
        <Space>
          <MedicineBoxOutlined />
          <span>Pregled lekara</span>
        </Space>
      }
    >
      <Alert
        message="Rezultati čekaju Vaš pregled"
        description={
          patientName
            ? `Pregledajte laboratorijske rezultate za pacijenta ${patientName} i odobrite ili odbijte.`
            : 'Pregledajte laboratorijske rezultate i odobrite ili odbijte.'
        }
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <div style={{ marginBottom: 16 }}>
        <Title level={5} style={{ marginBottom: 8 }}>
          Beleške pregleda
        </Title>
        <TextArea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Dodajte beleške o rezultatima (opciono)..."
          rows={3}
          disabled={submitting}
        />
      </div>

      <Space style={{ width: '100%' }} direction="vertical" size="middle">
        <Button
          type="primary"
          size="large"
          icon={<CheckCircleOutlined />}
          onClick={() => handleSubmit('APPROVED')}
          loading={submitting}
          block
          style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
        >
          Odobri rezultate
        </Button>
        <Button
          danger
          size="large"
          icon={<CloseCircleOutlined />}
          onClick={() => handleSubmit('REJECTED')}
          loading={submitting}
          block
        >
          Odbij rezultate
        </Button>
      </Space>

      <div style={{ marginTop: 12, textAlign: 'center' }}>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Nakon odobravanja, pacijent će moći da zatraži konsultaciju
        </Typography.Text>
      </div>
    </Card>
  )
}

export default DoctorReviewActions
